'use client';

import Link from 'next/link';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  applicationEndpoint,
  newApplicationIdentity,
  readCV,
  SubmissionError,
  submitApplication,
} from '@/lib/application-client';
import type { SubmissionMessages } from '@/lib/application-client';
import { trackEvent } from '@/lib/analytics';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { applyCopy } from '@/lib/i18n/pages/apply';
import type { ApplyCopy } from '@/lib/i18n/pages/apply';
import styles from './application.module.css';

type Answers = {
  position: string;
  name: string;
  email: string;
  phone: string;
  city: string;
  salaryExpectation: string;
  message: string;
  linkedin: string;
  portfolio: string;
};
type Field = keyof Answers;
type Step = Field | 'links' | 'cv' | 'review';
type Position = { value: string; label: string };
type Identity = ReturnType<typeof newApplicationIdentity>;
type Draft = { answers: Answers; step: number; identity: Identity };
const EMPTY: Answers = {
  position: '',
  name: '',
  email: '',
  phone: '',
  city: '',
  salaryExpectation: '',
  message: '',
  linkedin: '',
  portfolio: '',
};
const STEPS: Step[] = [
  'position',
  'name',
  'email',
  'phone',
  'city',
  'salaryExpectation',
  'message',
  'links',
  'cv',
  'review',
];
const DRAFT_KEY = 'datacode.application.v2';
const FILE_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};
/** Fields whose content is Latin by nature: they stay left to right in every locale. */
const LTR_FIELDS: Field[] = [
  'email',
  'phone',
  'salaryExpectation',
  'linkedin',
  'portfolio',
];

/** Fills {placeholders} in a copy string. */
function fillCopy(template: string, values: Record<string, string | number>) {
  return template.replace(/\{(\w+)\}/g, (_, key) => String(values[key] ?? ''));
}
/** Same, but the values are React nodes (for bidi isolation with <bdi>). */
function richCopy(template: string, values: Record<string, ReactNode>) {
  return template.split(/(\{\w+\})/g).map((part, index) => {
    const key = /^\{(\w+)\}$/.exec(part)?.[1];
    return key && key in values ? (
      <Fragment key={index}>{values[key]}</Fragment>
    ) : (
      part
    );
  });
}

function normalizeLink(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^[a-z][a-z\d+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
}
function fieldError(
  field: Field,
  value: string,
  e: ApplyCopy['errors'],
): string | null {
  const text = value.trim();
  if (['linkedin', 'portfolio'].includes(field)) {
    if (!text) return null;
    try {
      const url = new URL(normalizeLink(text));
      if (
        !['https:', 'http:'].includes(url.protocol) ||
        url.username ||
        url.password ||
        text.length > 500
      )
        throw new Error();
    } catch {
      return e.url;
    }
    return null;
  }
  if (!text) return e.required;
  if (field === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text))
    return e.email;
  if (
    field === 'phone' &&
    (!/^[+\d\s().-]+$/.test(text) || text.replace(/\D/g, '').length < 7)
  )
    return e.phone;
  if (['name', 'city'].includes(field) && text.length < 2)
    return e.minChars;
  if (field === 'message' && text.length < 10)
    return e.shortMessage;
  return null;
}
function loadDraft(): Draft | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
    if (
      !value?.answers ||
      !value.identity ||
      !/^[a-f\d-]{36}$/i.test(value.identity.applicationId) ||
      !/^[a-f\d]{64}$/.test(value.identity.submissionToken)
    )
      return null;
    if (
      !Object.keys(EMPTY).every(
        (key) =>
          typeof value.answers[key] === 'string' &&
          value.answers[key].length <= 5000,
      )
    )
      return null;
    return {
      ...value,
      step: Math.min(
        STEPS.length - 1,
        Math.max(0, Math.floor(Number(value.step) || 0)),
      ),
    };
  } catch {
    return null;
  }
}

export function TypeformApply({
  initialPosition,
}: {
  initialPosition?: string | null;
}) {
  const { locale, dir, href } = useLocale();
  const t = pageCopy(applyCopy, locale);
  const messages = useMemo<SubmissionMessages | undefined>(
    () =>
      locale === 'en'
        ? undefined
        : {
            readCv: t.errors.readCv,
            saveFailed: t.errors.saveFailed,
            unconfirmed: t.errors.unconfirmed,
            interrupted: t.errors.interrupted,
            localized: true,
          },
    [locale, t],
  );
  const [answers, setAnswers] = useState<Answers>(EMPTY);
  const [rawPositions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [draftChecked, setDraftChecked] = useState(false);
  const [resumed, setResumed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(-1);
  const [reference, setReference] = useState<string | null>(null);
  const [website, setWebsite] = useState('');
  const submitLock = useRef(false);
  const editBackup = useRef<Answers | null>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLHeadingElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const initializedRole = useRef(false);
  const step = STEPS[stepIndex];
  // Labels are shown in the page language; the value (the slug) is what is
  // stored in the draft and sent to the backend, and never changes.
  const positions = useMemo(
    () =>
      rawPositions.map((position) => ({
        value: position.value,
        label:
          locale === 'en'
            ? position.label
            : ((t.positions as Record<string, string>)[position.value] ??
              position.label),
      })),
    [rawPositions, locale, t],
  );
  const role = positions.find(
    (position) => position.value === answers.position,
  );
  const skipRole =
    !!initialPosition && initialPosition === answers.position && !!role;
  const displayedStep = stepIndex + 1 - (skipRole && stepIndex > 0 ? 1 : 0);
  const stepCount = STEPS.length - (skipRole && stepIndex > 0 ? 1 : 0);

  useEffect(() => {
    setIdentity(newApplicationIdentity());
    const saved = loadDraft();
    if (
      saved &&
      (!initialPosition || saved.answers.position === initialPosition)
    )
      setDraft(saved);
    setDraftChecked(true);
  }, [initialPosition]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    fetch(applicationEndpoint(), { signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        const body = await response.json();
        if (!Array.isArray(body.positions)) throw new Error();
        setPositions(
          body.positions.filter(
            (p: Position) =>
              typeof p.value === 'string' && typeof p.label === 'string',
          ),
        );
      })
      .catch(() => {
        if (!controller.signal.aborted) setLoadError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reload]);

  useEffect(() => {
    if (loading || loadError || initializedRole.current) return;
    if (
      initialPosition &&
      positions.some((position) => position.value === initialPosition)
    ) {
      setAnswers((previous) => ({ ...previous, position: initialPosition }));
      setStepIndex(1);
      initializedRole.current = true;
    } else if (initialPosition) {
      setError(t.errors.closedRole);
      initializedRole.current = true;
    }
  }, [initialPosition, loading, loadError, positions, t]);

  useEffect(() => {
    if (!draftChecked || draft || !identity || reference || loading) return;
    try {
      sessionStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ answers, step: stepIndex, identity }),
      );
    } catch {
      /* Storage can be disabled; the form still works. */
    }
  }, [answers, stepIndex, identity, draftChecked, draft, reference, loading]);

  useEffect(() => {
    if (!loading && !draft) {
      headerRef.current?.focus({ preventScroll: true });
      const bounds = headerRef.current?.getBoundingClientRect();
      if (bounds && (bounds.top < 0 || bounds.bottom > window.innerHeight))
        headerRef.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [stepIndex, loading, draft, reference]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () =>
      shellRef.current?.style.setProperty(
        '--application-height',
        `${viewport?.height || window.innerHeight}px`,
      );
    resize();
    viewport?.addEventListener('resize', resize);
    return () => viewport?.removeEventListener('resize', resize);
  }, []);
  useEffect(() => {
    if (!busy) return;
    const preventLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventLeave);
    return () => window.removeEventListener('beforeunload', preventLeave);
  }, [busy]);

  function change(field: Field, value: string) {
    setAnswers((previous) => ({ ...previous, [field]: value }));
    setError(null);
    setInvalidField(null);
  }
  function showError(message: string, field: string) {
    setError(message);
    setInvalidField(field);
  }
  function next() {
    if (busy) return;
    const fields: Field[] =
      step === 'links'
        ? ['linkedin', 'portfolio']
        : step === 'cv' || step === 'review'
          ? []
          : [step as Field];
    for (const field of fields) {
      const message = fieldError(field, answers[field], t.errors);
      if (message) return showError(message, field);
    }
    if (step === 'cv' && !file)
      return showError(t.errors.chooseCv, 'cv');
    setError(null);
    setInvalidField(null);
    setStepIndex(
      editing ? STEPS.length - 1 : Math.min(STEPS.length - 1, stepIndex + 1),
    );
    setEditing(false);
  }
  function chooseFile(candidate?: File) {
    if (!candidate) return;
    const extension = candidate.name.split('.').pop()?.toLowerCase() || '';
    if (
      !FILE_TYPES[extension] ||
      (candidate.type && candidate.type !== FILE_TYPES[extension])
    )
      return showError(t.errors.cvType, 'cv');
    if (candidate.size === 0 || candidate.size > 5 * 1024 * 1024)
      return showError(t.errors.cvSize, 'cv');
    if (candidate.name.length > 180)
      return showError(t.errors.cvName, 'cv');
    setFile(candidate);
    setError(null);
    setInvalidField(null);
  }
  async function submit() {
    if (submitLock.current || !identity) return;
    for (const field of Object.keys(EMPTY) as Field[]) {
      const message = fieldError(field, answers[field], t.errors);
      if (message) {
        setStepIndex(
          STEPS.indexOf(
            ['linkedin', 'portfolio'].includes(field) ? 'links' : field,
          ),
        );
        return showError(message, field);
      }
    }
    if (!file) {
      setStepIndex(STEPS.indexOf('cv'));
      return showError(t.errors.cvAgain, 'cv');
    }
    submitLock.current = true;
    setBusy(true);
    setError(null);
    setProgress(-1);
    try {
      const cvBase64 = await readCV(file, messages);
      setProgress(0);
      const id = await submitApplication(
        {
          ...answers,
          linkedin: normalizeLink(answers.linkedin),
          portfolio: normalizeLink(answers.portfolio),
          ...identity,
          website,
          cvBase64,
          cvFileName: file.name,
          cvContentType: FILE_TYPES[file.name.split('.').pop()!.toLowerCase()],
        },
        setProgress,
        messages,
      );
      setReference(id);
      try {
        sessionStorage.removeItem(DRAFT_KEY);
      } catch {
        /* Optional browser storage. */
      }
      trackEvent('apply_submit', {
        status: 'success',
        position: answers.position,
      });
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : t.errors.submitFailed,
      );
      if (failure instanceof SubmissionError && failure.field) {
        const target = ['linkedin', 'portfolio'].includes(failure.field)
          ? 'links'
          : failure.field;
        const index = STEPS.indexOf(target as Step);
        if (index >= 0) setStepIndex(index);
        setInvalidField(failure.field);
        if (failure.field === 'position') {
          setAnswers((previous) => ({ ...previous, position: '' }));
          setReload((value) => value + 1);
        }
      }
      trackEvent('apply_submit', {
        status: 'error',
        position: answers.position,
      });
    } finally {
      submitLock.current = false;
      setBusy(false);
    }
  }
  function restoreDraft() {
    if (!draft) return;
    const active = rawPositions.some(
      (position) => position.value === draft.answers.position,
    );
    setAnswers({
      ...draft.answers,
      position: active ? draft.answers.position : '',
    });
    setIdentity(draft.identity);
    setStepIndex(active ? Math.min(draft.step, STEPS.indexOf('cv')) : 0);
    setResumed(true);
    setDraft(null);
    setError(
      active
        ? null
        : t.errors.draftRoleClosed,
    );
  }

  const textInput = (
    field: Field,
    options: {
      placeholder?: string;
      type?: string;
      autoComplete?: string;
      max?: number;
    } = {},
  ) => {
    const ltr = LTR_FIELDS.includes(field);
    return (
      <div className={styles.field} key={field}>
        <label htmlFor={`apply-${field}`}>{t.labels[field]}</label>
        <input
          id={`apply-${field}`}
          name={field}
          value={answers[field]}
          onChange={(event) => change(field, event.target.value)}
          type={options.type || 'text'}
          dir={ltr ? 'ltr' : 'auto'}
          className={ltr ? styles.ltrInput : styles.autoInput}
          autoComplete={options.autoComplete}
          autoCapitalize={
            ['email', 'linkedin', 'portfolio'].includes(field)
              ? 'none'
              : 'words'
          }
          inputMode={
            ['linkedin', 'portfolio'].includes(field)
              ? 'url'
              : field === 'email'
                ? 'email'
                : field === 'phone'
                  ? 'tel'
                  : undefined
          }
          spellCheck={ltr ? false : undefined}
          placeholder={options.placeholder}
          maxLength={options.max || 100}
          aria-invalid={invalidField === field}
          aria-describedby={
            invalidField === field ? 'application-error' : undefined
          }
          enterKeyHint="next"
        />
      </div>
    );
  };
  const arrow = (glyph: string) => (
    <span aria-hidden="true" className={styles.mirror}>
      {glyph}
    </span>
  );
  const isolate = (value: string, direction: 'ltr' | 'auto' = 'ltr') => (
    <bdi dir={direction}>{value}</bdi>
  );

  return (
    <div
      ref={shellRef}
      dir={dir}
      lang={locale}
      className={`self-styled careers-application ${styles.shell}`}
    >
      <header className={styles.header}>
        <Link
          href={href('/')}
          className={styles.brand}
          aria-label={t.chrome.brandAria}
        >
          datacode<span>®</span>
        </Link>
        <Link href={href('/careers')} className={styles.exit}>
          {t.chrome.allCareers} {arrow('↗')}
        </Link>
      </header>
      <div className={styles.layout}>
        <aside className={styles.intro}>
          <span className={styles.eyebrow}>{t.chrome.introEyebrow}</span>
          <h1>
            {t.chrome.introTitleA}
            <br />
            <span>{t.chrome.introTitleB}</span>
          </h1>
          <p>
            {t.chrome.introLine1}
            <br />
            {t.chrome.introLine2}
          </p>
          <div className={styles.introFoot}>
            <span>{t.chrome.footLeft}</span>
            <span>{t.chrome.footRight}</span>
          </div>
        </aside>
        <div className={styles.formArea}>
          {reference ? (
            <section className={styles.success} aria-live="polite">
              <span className={styles.successIcon} aria-hidden="true">
                ✓
              </span>
              <span className={styles.eyebrow}>{t.success.eyebrow}</span>
              <h2 ref={headerRef} tabIndex={-1}>
                {t.success.title}
              </h2>
              <p>
                {richCopy(t.success.body, {
                  name: isolate(answers.name.split(' ')[0], 'auto'),
                  role: isolate(role?.label || answers.position, 'auto'),
                })}
              </p>
              <p className={styles.reference}>
                {t.success.reference}
                <br />
                <strong dir="ltr">{reference}</strong>
              </p>
              <Link href={href('/careers')} className={styles.primary}>
                {t.buttons.backToCareers} {arrow('↗')}
              </Link>
            </section>
          ) : loading ? (
            <p role="status" className={styles.loading}>
              {t.states.loading}
            </p>
          ) : loadError ? (
            <div className={styles.loading}>
              <h2>{t.states.loadFailTitle}</h2>
              <p role="alert">{t.states.loadFailBody}</p>
              <button
                className={styles.primary}
                onClick={() => setReload((value) => value + 1)}
              >
                {t.buttons.tryAgain}
              </button>
            </div>
          ) : !positions.length ? (
            <div className={styles.loading}>
              <h2>{t.states.noRolesTitle}</h2>
              <p>{t.states.noRolesBody}</p>
              <Link href={href('/careers')}>{t.buttons.backToCareers}</Link>
            </div>
          ) : draft ? (
            <section className={styles.loading}>
              <span className={styles.eyebrow}>{t.states.draftEyebrow}</span>
              <h2>{t.states.draftTitle}</h2>
              <p>{t.states.draftBody}</p>
              <button className={styles.primary} onClick={restoreDraft}>
                {t.buttons.resume} {arrow('→')}
              </button>
              <button
                className={styles.secondary}
                onClick={() => {
                  setDraft(null);
                  setIdentity(newApplicationIdentity());
                }}
              >
                {t.buttons.startFresh}
              </button>
            </section>
          ) : (
            <form
              className={styles.form}
              onSubmit={(event) => {
                event.preventDefault();
                if (step !== 'review') next();
              }}
              noValidate
              aria-busy={busy}
            >
              <div className={styles.progressHeader}>
                <span>{role?.label || t.progress.yourApplication}</span>
                <span
                  aria-label={fillCopy(t.progress.stepAria, {
                    step: displayedStep,
                    total: stepCount,
                  })}
                >
                  <bdi dir="ltr">
                    {String(displayedStep).padStart(2, '0')}{' '}
                    <span className={styles.muted}>
                      / {String(stepCount).padStart(2, '0')}
                    </span>
                  </bdi>
                </span>
              </div>
              <div
                className={styles.progress}
                role="progressbar"
                aria-label={t.progress.barAria}
                aria-valuemin={0}
                aria-valuemax={stepCount}
                aria-valuenow={displayedStep}
              >
                <span
                  style={{ width: `${(displayedStep / stepCount) * 100}%` }}
                />
              </div>
              <div className={styles.question} key={step}>
                <span className={styles.eyebrow}>
                  {step === 'links'
                    ? t.eyebrow.optional
                    : step === 'review'
                      ? t.eyebrow.review
                      : step === 'cv'
                        ? t.eyebrow.cv
                        : t.eyebrow.know}
                </span>
                <h2 ref={headerRef} tabIndex={-1}>
                  {(t.questions as Record<string, string>)[step]}
                </h2>
                {step === 'position' && (
                  <div
                    className={styles.choices}
                    role="group"
                    aria-label={t.progress.openRoles}
                  >
                    {positions.map((position, index) => (
                      <button
                        type="button"
                        className={styles.choice}
                        key={position.value}
                        aria-pressed={answers.position === position.value}
                        onClick={() => change('position', position.value)}
                      >
                        <span className={styles.choiceLetter} dir="ltr">
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span>{position.label}</span>
                        {answers.position === position.value ? (
                          <span aria-hidden="true">✓</span>
                        ) : (
                          arrow('↗')
                        )}
                      </button>
                    ))}
                  </div>
                )}
                {step === 'name' &&
                  textInput('name', {
                    placeholder: t.placeholders.name,
                    autoComplete: 'name',
                    max: 120,
                  })}
                {step === 'email' && (
                  <>
                    <p>{t.hints.email}</p>
                    {textInput('email', {
                      placeholder: t.placeholders.email,
                      type: 'email',
                      autoComplete: 'email',
                      max: 254,
                    })}
                  </>
                )}
                {step === 'phone' &&
                  textInput('phone', {
                    placeholder: t.placeholders.phone,
                    type: 'tel',
                    autoComplete: 'tel',
                    max: 40,
                  })}
                {step === 'city' && (
                  <>
                    <p>{t.hints.city}</p>
                    {textInput('city', {
                      placeholder: t.placeholders.city,
                      autoComplete: 'address-level2',
                    })}
                  </>
                )}
                {step === 'salaryExpectation' && (
                  <>
                    <p>{t.hints.salaryExpectation}</p>
                    {textInput('salaryExpectation', {
                      placeholder: t.placeholders.salaryExpectation,
                    })}
                  </>
                )}
                {step === 'message' && (
                  <div className={styles.field}>
                    <p>
                      {role?.label
                        ? richCopy(t.hints.messageRole, {
                            role: isolate(role.label, 'auto'),
                          })
                        : t.hints.messageTeam}
                    </p>
                    <label htmlFor="apply-message">{t.labels.message}</label>
                    <textarea
                      id="apply-message"
                      dir="auto"
                      className={styles.autoInput}
                      value={answers.message}
                      onChange={(event) =>
                        change('message', event.target.value)
                      }
                      rows={5}
                      maxLength={5000}
                      placeholder={t.placeholders.message}
                      aria-invalid={invalidField === 'message'}
                      aria-describedby={
                        invalidField === 'message'
                          ? 'application-error'
                          : undefined
                      }
                    />
                    <span className={styles.hint}>
                      {richCopy(t.hints.charCount, {
                        counter: (
                          <bdi dir="ltr">
                            {answers.message.length} / 5,000
                          </bdi>
                        ),
                      })}
                    </span>
                  </div>
                )}
                {step === 'links' && (
                  <>
                    <p>{t.hints.links}</p>
                    {textInput('linkedin', {
                      placeholder: t.placeholders.linkedin,
                      max: 500,
                    })}
                    {textInput('portfolio', {
                      placeholder: t.placeholders.portfolio,
                      max: 500,
                    })}
                  </>
                )}
                {step === 'cv' && (
                  <>
                    <p>{t.hints.cv}</p>
                    {resumed && !file && (
                      <p className={styles.hint}>{t.hints.cvRestored}</p>
                    )}
                    <div
                      className={styles.upload}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={(event) => {
                        event.preventDefault();
                        chooseFile(event.dataTransfer.files[0]);
                      }}
                    >
                      <span aria-hidden="true" className={styles.uploadIcon}>
                        ↑
                      </span>
                      <strong>
                        {file ? isolate(file.name) : t.upload.title}
                      </strong>
                      <span>
                        {file
                          ? richCopy(t.upload.ready, {
                              size: isolate(
                                (file.size / 1024 / 1024).toFixed(2),
                              ),
                            })
                          : t.upload.subtitle}
                      </span>
                      <input
                        ref={fileRef}
                        type="file"
                        id="apply-cv"
                        aria-label={t.upload.aria}
                        accept=".pdf,.doc,.docx"
                        className={styles.fileInput}
                        onChange={(event) => {
                          chooseFile(event.target.files?.[0]);
                          event.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        className={styles.uploadButton}
                        onClick={() => fileRef.current?.click()}
                      >
                        {file ? t.upload.replace : t.upload.choose}{' '}
                        {arrow('↗')}
                      </button>
                      {file && (
                        <button
                          type="button"
                          className={styles.secondary}
                          onClick={() => setFile(null)}
                        >
                          {t.upload.remove}
                        </button>
                      )}
                    </div>
                  </>
                )}
                {step === 'review' && (
                  <>
                    <p>{t.hints.review}</p>
                    <dl className={styles.review}>
                      {(Object.keys(EMPTY) as Field[]).map((field) => (
                        <div key={field}>
                          <dt>{t.labels[field]}</dt>
                          <dd>
                            {field === 'position'
                              ? role?.label
                              : answers[field]
                                ? isolate(
                                    answers[field],
                                    LTR_FIELDS.includes(field) ? 'ltr' : 'auto',
                                  )
                                : t.review.notAdded}
                          </dd>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              editBackup.current = { ...answers };
                              setEditing(true);
                              setStepIndex(
                                STEPS.indexOf(
                                  ['linkedin', 'portfolio'].includes(field)
                                    ? 'links'
                                    : field,
                                ),
                              );
                              setError(null);
                            }}
                            aria-label={fillCopy(t.review.editField, {
                              field: t.labels[field],
                            })}
                          >
                            {t.review.edit}
                          </button>
                        </div>
                      ))}
                      <div>
                        <dt>{t.labels.cv}</dt>
                        <dd>
                          {file ? isolate(file.name) : t.review.cvMissing}
                        </dd>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => {
                            editBackup.current = { ...answers };
                            setEditing(true);
                            setStepIndex(STEPS.indexOf('cv'));
                          }}
                          aria-label={t.review.editCv}
                        >
                          {t.review.edit}
                        </button>
                      </div>
                    </dl>
                    <p className={styles.privacy}>{t.hints.privacy}</p>
                  </>
                )}
              </div>
              <div className={styles.honeypot} aria-hidden="true">
                <label htmlFor="apply-website">{t.states.honeypot}</label>
                <input
                  id="apply-website"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>
              {error && (
                <p id="application-error" role="alert" className={styles.error}>
                  {error}
                </p>
              )}
              <footer className={styles.controls}>
                {busy ? (
                  <div className={styles.submitting} role="status">
                    <span>
                      {progress < 0
                        ? t.states.preparing
                        : progress < 100
                          ? richCopy(t.states.sending, {
                              percent: isolate(String(progress)),
                            })
                          : t.states.saving}
                    </span>
                    <p>{t.states.keepOpen}</p>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      className={styles.back}
                      disabled={stepIndex === 0}
                      onClick={() => {
                        if (editing && editBackup.current)
                          setAnswers(editBackup.current);
                        setStepIndex(
                          editing ? STEPS.length - 1 : stepIndex - 1,
                        );
                        setEditing(false);
                        setError(null);
                        setInvalidField(null);
                      }}
                    >
                      {arrow('←')}{' '}
                      {editing ? t.buttons.cancelEdit : t.buttons.back}
                    </button>
                    <div className={styles.actions}>
                      {step === 'links' && (
                        <button
                          type="button"
                          className={styles.secondary}
                          onClick={() => {
                            setAnswers((previous) => ({
                              ...previous,
                              linkedin: '',
                              portfolio: '',
                            }));
                            setStepIndex(
                              editing ? STEPS.length - 1 : stepIndex + 1,
                            );
                            setEditing(false);
                            setError(null);
                          }}
                        >
                          {t.buttons.skip}
                        </button>
                      )}
                      {step === 'review' ? (
                        <button
                          type="button"
                          className={styles.primary}
                          onClick={submit}
                        >
                          {t.buttons.send} {arrow('↗')}
                        </button>
                      ) : (
                        <button
                          type="submit"
                          className={styles.primary}
                          disabled={step === 'position' && !answers.position}
                        >
                          {editing ? t.buttons.saveAnswer : t.buttons.continue}{' '}
                          {arrow('→')}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </footer>
              <p className={styles.sessionNote}>{t.hints.sessionNote}</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
